import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { Spinner, Card } from '@tcc/crate';
import ScenariosTable from './scenarios-table/ScenariosTable';
import ZoneTable from './ZoneTable';
import DeadZoneWarning from './DeadZoneWarning';
import ActiveScenarioSummaryWithDensityTool from './ActiveScenarioSummaryWithDensityTool';
import ScenariosListContainer from 'advanced/scenarios/ScenariosListContainer';

import { getMessage } from 'util/locale';
import { scriptIsFetching as getScriptIsFetching, getSelectedZoneIds } from 'current/selectors';
import { getActiveCropNominalWeight } from 'current/currentSelectors';
import { getActiveScenario } from 'advanced/scenarios/selectors';
import { getPlantingPlanParams } from 'planting-plan-form/selectors';
import {
  getProductValidationValues,
  allowDecimalByCountry as allowDecimalByCountrySelector
} from 'user/selectors';
import { hasDeadZone as hasDeadZoneSelector } from 'advanced/selectors';
import { convertScenarioForLocale } from 'advanced/scenarios/util/convertScenarioForLocale';
import { selectZone, removeActiveZoneSelection } from 'current/uiwidgets';

const baseString = 'vra-active-scenario-summary';
const classString = classNames({
  'vra-review-scenarios': true,
  [`${baseString}`]: true,
  'vra-create-script-screen__review-scenarios': true
});

const inProgressClassString = classNames({
  [`${baseString}`]: true,
  [`${baseString}--pending`]: true,
});

import {
  formatPlaceholderZoneRows,
  formatZoneRowsForScenario,
  getAreaHeaderText,
  getRateHeaderText,
  getYieldHeaderText
} from './zoneTableUtil';

import { inferUnit } from 'util/unitConversion';

const ReviewScenariosWithDensityTool = () => {
  const scriptIsFetching = useSelector(getScriptIsFetching);
  const activeScenario = useSelector(getActiveScenario);
  const cropNominalWeight = useSelector(getActiveCropNominalWeight);
  const convertedScenario = convertScenarioForLocale({ scenario: activeScenario, cropNominalWeight });
  const allowDecimalByCountry = useSelector(allowDecimalByCountrySelector);
  const selectedZoneIds = useSelector(getSelectedZoneIds);
  const hasDeadZone = useSelector(hasDeadZoneSelector);
  const numberOfZones = useSelector(getPlantingPlanParams)['num-zones'] || 4;
  const productValidationValues = useSelector(getProductValidationValues);
  const dispatch = useDispatch();

  const rows = scriptIsFetching
    ? formatPlaceholderZoneRows(numberOfZones)
    : formatZoneRowsForScenario(
      {
        scenario: convertedScenario,
        productValidationValues,
        selectedZoneIds,
        allowDecimalByCountry
      }
    );
  let content;
  
  if (scriptIsFetching) {
    content = (
      <section className={classString}>
        <Card className={inProgressClassString}>
          <Spinner size={24} />
          <span>{getMessage('text__in-progress--create-advanced-prescription-management-zones')}</span>
        </Card>
      </section>
    );
  } else if (!activeScenario) {
    content = (
      <section className={classString}>
        <Card>
          <header>{getMessage('text__no-data')}</header>
        </Card>
      </section>
    );
  } else {
    content = (
      <section className='vra-create-script-screen__review-scenarios'>
        <ScenariosTable/>
        <ActiveScenarioSummaryWithDensityTool scenario={convertedScenario}/>
        <ZoneTable
          areaHeaderText={getAreaHeaderText(inferUnit('area').symbol.quantitySpecific)}
          onClickRow={(zoneId, isSelected) => {
            dispatch(removeActiveZoneSelection());
            if (!isSelected) {
              dispatch(selectZone({ mapWasClickedOnZoneSelect: false, zoneId }));
            }
          }}
          rateHeaderText={getRateHeaderText(inferUnit('seed-density').symbol.quantitySpecific)}
          rows={rows}
          yieldHeaderText={getYieldHeaderText(inferUnit('area-density.yield').symbol.quantitySpecific)}
          isDensityToolEnabled
        />
        <DeadZoneWarning hasDeadZone={hasDeadZone} />
        <ScenariosListContainer />
      </section>
    );
  }

  return content;
};

export default ReviewScenariosWithDensityTool;
